import { useState } from 'react'
import { ChevronDown, ChevronUp, Users, Target, MapPin, Award, UserCheck, Megaphone, Home, PoundSterling, GraduationCap, Clock, Settings, Brain, Shield, ClipboardCheck, FileCheck, Building, TrendingUp, CreditCard, Quote } from 'lucide-react'

// Sections data
export const sections = [
  {
    id: 'objective',
    title: 'Objective',
    icon: Target,
    content: {
      overview: 'Ensure the timely mobilisation of a fully trained, compliant and route-ready driver workforce to support Transport UK operations at Huddersfield depot commencing April 2027.',
      details: [
        'Delivering drivers who meet all regulatory and operational requirements (PCV, CPC, Medical and Training)',
        'Retaining flexibility to respond to TUPE outcomes and the operational requirements of Huddersfield depot and associated route allocations.',
        'Providing a dependable, low-risk mobilisation solution that safeguards service continuity and protects Transport UK\'s operational performance during a period of significant structural change'
      ],
      context: 'We understand that early franchise implementation is operationally sensitive. The first weeks of transition often involve shifting workforce numbers, compressed onboarding timelines and increased public scrutiny. Our role is to reduce that pressure by ensuring workforce readiness is controlled, visible and predictable.',
      summary: 'What differentiates C9 is not simply access to drivers, but a disciplined mobilisation process. Our integrated systems, structured onboarding stages and live reporting tools give Transport UK\'s operational leadership clarity over driver readiness, compliance progression and deployment status — helping maintain stability throughout the transition.'
    }
  },
  {
    id: 'position',
    title: 'Strategic Position & Risks',
    icon: MapPin,
    content: {
      overview: 'The Huddersfield depot mobilisation represents a requirement for approximately 70 drivers to support operations commencing April 2027. Mobilisation planning must account for recruitment timelines, potential TUPE outcomes and short-notice workforce fluctuations. Our approach focuses on controlled onboarding, scalable supply and clear operational visibility to ensure service continuity from day one.',
      risks: [
        'Uncertainty around TUPE transfer volumes',
        'Driver attrition following transfer',
        'Competing operator retention incentives',
        'Phased depot transitions creating temporary resourcing gaps',
        'Public and political scrutiny associated with Combined Authority transport reform',
        'Risk of service disruption during early mobilisation phases'
      ],
      mitigation: 'C9\'s approach is designed to manage these risks practically and proactively. We focus on measured workforce scaling, controlled onboarding stages and clear operational reporting so that potential gaps are identified early rather than reactively addressed. Our objective is not simply to supply drivers, but to support Transport UK in maintaining reliable service delivery and protecting its reputation during a high-profile public sector transition.'
    }
  },
  {
    id: 'mobilisation',
    title: 'Driver Mobilisation Overview',
    icon: Users,
    content: {
      requirements: [
        { label: 'Estimated Driver Requirement', value: '71 drivers (subject to final operational requirements) for Huddersfield depot.' },
        { label: 'Mobilisation Timeline', value: 'Driver commencement: Sunday 4 April 2027. Onboarding commencement: Approximately two weeks prior to service launch.' },
        { label: 'Depot Location', value: 'Huddersfield – West Yorkshire' },
        { label: 'Working Patterns', value: 'Full-time, part-time, weekend-only' },
        { label: 'Compliance Requirements', value: 'Valid PCV Licence, CPC, Medical & Training, Route learning, Route risk assessments, Vehicle type training, Company induction' }
      ],
      mobilisationModel: 'Our mobilisation model is underpinned by defined onboarding gateways, staged compliance verification and structured deployment planning. Each driver progresses through controlled assessment checkpoints, ensuring regulatory assurance and operational readiness before route allocation.'
    }
  },
  {
    id: 'expertise',
    title: 'C9 Expertise & Experience',
    icon: Award,
    content: {
      overview: 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, supported by a proven track record of delivery across complex, multi-depot operations.',
      achievements: [
        'TUPE volatility and late-stage driver number shifts',
        'Short-notice contingency supply requirements',
        'Operational pressures during early franchise launch weeks',
        'Media and political scrutiny associated with Combined Authority transitions',
        'The importance of structured onboarding and route-readiness assurance'
      ],
      experience: 'Members of our leadership team were directly involved in supporting the Bee Network transition in Manchester. That experience provided valuable insight into large-scale driver mobilisation, particularly in managing short-notice demand, onboarding at pace and maintaining compliance under operational pressure.',
      beeNetworkMetrics: 'While the driver market in West Yorkshire differs from Greater Manchester, the operational approach, mobilisation structure and lessons learned remain directly applicable to this requirement.',
      innovation: 'This experience includes supporting large-scale multi-depot mobilisation programmes where driver volumes, onboarding timelines and operational requirements evolved rapidly during implementation. Our approach is built around risk mitigation, phased scaling capability, compliance-led onboarding and real-time operational oversight — ensuring a resilient and low-risk mobilisation solution for the Huddersfield depot mobilisation.'
    }
  },
  {
    id: 'management',
    title: 'Account Management & Support Structure',
    icon: UserCheck,
    content: {
      overview: 'We will deliver a robust and proactive account management and support structure to ensure a seamless partnership with Transport UK for the Huddersfield depot mobilisation. Our approach is hands-on, collaborative, and performance-driven.',
      keyFeatures: [
        'Dedicated Mobilisation Team: A specialist team overseeing all aspects of mobilisation and delivery, ensuring that all requirements are met on time and to the highest standard.',
        'On-Site Management: Experienced personnel will be present at the depot to manage operations, support staff, and maintain consistent communication with all stakeholders.',
        'Real-Time Reporting: Live dashboards and performance reports will provide full visibility of progress and KPIs, enabling rapid response to any challenges.',
        '24/7 Operational Support: 24/7 operational support throughout the West Yorkshire mobilisation phase, including on-site presence during key deployment stages.',
        'Stakeholder Collaboration: We prioritise transparent communication and active engagement with all parties involved to ensure alignment and smooth delivery.',
        'Continuous Monitoring & Improvement: Ongoing performance evaluation and adaptive support strategies will maintain operational excellence throughout the project lifecycle.'
      ],
      leadership: {
        overview: 'Leadership & Local Commitment:',
        details: [
          'Alfie (Chief Executive) will lead on strategic delivery and oversight, ensuring alignment with franchise goals and overall project success.',
          'Mustafa Ali (Client Success Manager) will serve as the dedicated Account Manager, coordinating daily operations and acting as the primary day-to-day point of contact.',
          'Senior leadership presence maintained throughout the Huddersfield mobilisation phase, with direct experience from previous large-scale franchising mobilisation programmes.'
        ]
      },
      crossDepartmental: {
        overview: 'Cross-Departmental Support:',
        description: 'We will allocate a dedicated mobilisation team member from each of the following departments to ensure full-spectrum support:',
        departments: [
          {
            name: 'Compliance',
            icon: 'FileCheck',
            description: 'Ensuring all regulatory requirements, licensing, and safety standards are met throughout the mobilisation process.'
          },
          {
            name: 'Relocation & Housing',
            icon: 'Building',
            description: 'Managing accommodation arrangements and relocation support for drivers joining from outside the immediate region.'
          },
          {
            name: 'Marketing',
            icon: 'TrendingUp',
            description: 'Driver attraction campaigns, recruitment marketing, and brand representation throughout the mobilisation period.'
          },
          {
            name: 'Payroll',
            icon: 'CreditCard',
            description: 'Seamless payment processing, benefits administration, and financial support coordination for all deployed drivers.'
          }
        ]
      },
      sustainability: {
        title: 'Long-Term Sustainability:',
        description: 'Should resource support be required beyond the initial four-month mobilisation period, we will appoint a local Account Manager based in West Yorkshire. This individual will be fully onboarded and trained by Alfie and Mustafa, replicating our successful model used during previous franchising mobilisation programmes.'
      },
      mobilisationLed: 'Unlike traditional agencies, C9 operates a mobilisation-led model rather than a reactive supply model. Dedicated account leadership, structured reporting and live dashboard visibility provide assurance to senior operational teams throughout the transition period.'
    }
  },
  {
    id: 'assessment',
    title: 'Assessment & Training Process',
    icon: ClipboardCheck,
    content: {
      overview: 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment.',
      stage1: {
        title: 'Stage 1: Name Check & Document Verification',
        details: 'ID, right-to-work checks, licence verification, and initial screening'
      },
      stage2: {
        title: 'Stage 2: Driving & Medical Assessment',
        details: 'On-road driving assessment by qualified assessors, drug & alcohol testing if required'
      },
      induction: {
        title: 'Induction',
        details: 'Induction within one week of successful assessment, introduction to depot procedures, ticketing systems, company policies'
      },
      route_learning: {
        title: 'Route Learning',
        details: 'Route learning plan provided; recommend starting with limited set of 3–5 routes for familiarity'
      },
      mentoring: {
        title: 'Mentoring',
        details: 'Drivers paired with experienced mentors or route guides for hands-on route training'
      },
      signoff: {
        title: 'Final Sign-off',
        details: 'Final route competency and area knowledge confirmed by designated mentor or supervisor'
      },
      structuredProcess: 'This structured process ensures every deployed driver is compliance-cleared, route-ready and performance-assured prior to entering live service, significantly reducing operational risk during franchise transitions.'
    }
  },
  {
    id: 'attraction',
    title: 'Driver Attraction Strategy',
    icon: Megaphone,
    content: {
      overview: 'Multi-channel driver attraction campaign with relocation strategy playing a pivotal role in attracting drivers outside of commutable distance.',
      audiences: {
        primary: [
          'Local PCV Drivers working for other bus and coach operators',
          'Drivers displaced from depot closures or operator reductions due to end of seasonal work'
        ],
        secondary: [
          'Local PCV holders who are currently inactive, semi-retired or have certain availability',
          'Part-time drivers overlooked by other operators who were unwilling to consider flexible working',
          'Relocation drivers from outside the immediate region'
        ]
      },
      channels: [
        'Digital Advertising: Indeed, CV-Library, social media (Facebook/Instagram), targeted PPC ads',
        'Job Fairs & Roadshows: Targeted recruitment campaigns across West Yorkshire and surrounding regions to support Huddersfield depot mobilisation',
        'Driver Referral Programme: Bonuses paid by C9 to existing drivers for successful referrals',
        'Industry Partnerships: Collaboration with PCV training schools, employment schemes, and logistics staffing groups'
      ]
    }
  },
  {
    id: 'relocation',
    title: 'Relocation Model',
    icon: Home,
    content: {
      overview: 'To widen the available talent pool and attract experienced drivers from outside the immediate region, we offer a structured relocation offering completely managed by C9 Recruitment.',
      model: 'Our relocation model has been successfully deployed on previous large-scale mobilisation projects, providing additional workforce flexibility where local driver supply is constrained. This approach allows us to supplement local recruitment with a broader national candidate pool, ensuring operational requirements can still be met where regional availability is limited. Accommodation paid for and managed by C9 Recruitment and invoiced back at cost.',
      accommodation: [
        'Fully serviced, high-quality accommodation sourced via professional property management company',
        'Properties typically range from 2 to 6 bedrooms, with each driver allocated a private bedroom where possible',
        'Communal areas include shared kitchen, bathroom(s), and living space, maintained to consistent standard',
        'Flexible 28-day rolling contracts to accommodate changing operational needs',
        'Costs fully inclusive of utilities (electricity, water, gas, internet, council tax)',
        'Security deposits may be required, subject to individual property terms',
        'Strategically located for ease of access to depots or route start points where possible'
      ],
      identification: 'Relocation drivers identified at initial vetting stage for clear understanding before onboarding completion.',
      safetyCompliance: 'All properties fully compliant with UK safety regulations, with up-to-date Gas Safety Certificates, Electrical Installation Condition Reports (EICR), and comprehensive Fire Safety Documentation maintained and readily available for inspection'
    }
  },
  {
    id: 'proposition',
    title: 'Competitive & Attractive Driver Package',
    icon: PoundSterling,
    content: {
      overview: 'Competitive and attractive driver proposition designed to attract and retain quality drivers throughout the mobilisation period.',
      description: 'Our driver offering is structured to ensure consistency, reliability and retention during mobilisation.',
      benefits: [
        'Guaranteed 50 hours per week (aligned with franchising mobilisation requirements)',
        'Structured shift patterns aligned to depot operations',
        'Ongoing work stability throughout the engagement period',
        'Competitive pay aligned to the West Yorkshire driver market'
      ],
      marketNote: 'Driver rates and full cost breakdown are detailed within the accompanying rate card to support Transport UK\'s commercial modelling.'
    }
  },
  {
    id: 'rate-card',
    title: 'Driver Rate Card – Huddersfield Mobilisation',
    icon: CreditCard,
    content: {
      intro: 'To support Transport UK\'s commercial modelling for the Huddersfield mobilisation, the following rate structure provides a clear breakdown of wage costs, statutory contributions and charge rates.',
      context: 'Flat Rate: £16.50 pay rate, £24.91 charge rate. Accommodation Allowance: £168.00 pay rate, £199.08 charge rate.',
      umbrellaNote: 'These rates reflect current market conditions and are provided to support initial cost modelling. Rates may require adjustment closer to mobilisation depending on driver availability, market conditions and any changes to statutory employment costs. For umbrella engagements, the standard driver charge rate includes the C9 management fee. The accommodation allowance reflects cost of wage only and does not include an additional C9 management fee.',
      rateTable: [
        {
          type: 'Flat Rate',
          payRate: '£16.50',
          holidayPay: '£1.99',
          nationalInsurance: '£2.77',
          apprenticeshipLevy: '£0.09',
          pension: '£0.55',
          costOfWage: '£21.91',
          managementFee: '£3.00',
          chargeRate: '£24.91'
        },
        {
          type: 'Accommodation Allowance',
          payRate: '£168.00',
          holidayPay: '£0.00',
          nationalInsurance: '£25.20',
          apprenticeshipLevy: '£0.84',
          pension: '£5.04',
          costOfWage: '£199.08',
          managementFee: '£0.00',
          chargeRate: '£199.08'
        }
      ],
      notes: [
        'PAYE driver rates include full statutory cost breakdown as outlined above',
        'Flat rate applies to standard driver working hours',
        'Guaranteed 50 hours per week forms the basis of cost modelling',
        'Accommodation allowance applies where relocation support is required and is charged at cost',
        'Umbrella driver engagements follow the same charge structure for standard hours',
        'Rates are based on current market conditions and may require adjustment closer to mobilisation'
      ]
    }
  },
  {
    id: 'training',
    title: 'Assessment & Training (OPTIONAL Ridgeway * Beverley Bell Partnership)',
    icon: GraduationCap,
    content: {
      partnership: 'Ridgeway Training selected as preferred training provider. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader services.',
      leadership: 'Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector.',
      expertise: 'Expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers.',
      availability: 'This partnership is offered as an optional service enhancement, with both Ridgeway Training and Beverley Bell Consulting & Training available to provide comprehensive training solutions.',
      courses: [
        'OLAT (Operator Licence Awareness Training)',
        'TM CPC (Transport Manager Certificate of Professional Competence)',
        'First Aid training',
        'Mental Health awareness',
        'Driver CPC modules',
        'E-learning driver platform (in development)'
      ]
    }
  },
  {
    id: 'supply',
    title: 'Supply Model',
    icon: Clock,
    content: {
      commitment: 'Minimum four-month driver engagement period, aligning with Transport UK\'s anticipated contract duration while permanent recruitment is undertaken.',
      guarantee: 'All drivers supplied under this mobilisation will operate on a guaranteed 50-hour working week to ensure consistency, availability and operational stability during the transition period.',
      timeline: 'Assessments, training, and onboarding will take approximately 3 to 6 weeks, depending on specific training requirements.',
      mobilisation: 'Remaining period begins with mobilisation, drivers required consistently for first 1 to 4 weeks while TUPE drivers complete transfer and training.',
      standby: 'In the event that full cohort of drivers transfers across, our drivers remain on standby for remainder of engagement period.',
      flexibility: 'Drivers continue to be paid guaranteed hours and remain available to cover sickness or holiday absences.',
      windDown: 'Driver numbers can then be reduced in a controlled and phased manner as permanent recruitment is completed.'
    }
  },
  {
    id: 'onsite',
    title: 'Onsite Management',
    icon: Settings,
    content: {
      presence: 'Dedicated C9 operations presence at depot(s) to ensure seamless service delivery.',
      reporting: 'Live reporting and performance dashboard providing real-time insights.',
      monitoring: 'Continuous performance monitoring and support structure.',
      support: 'Real-time support structure ensuring immediate response to operational needs.'
    }
  },
  {
    id: 'technology',
    title: 'AI & Technology Integration',
    icon: Brain,
    content: {
      systems: [
        'Automated DVLA checks for drivers completed via direct DVLA API',
        'Comprehensive Applicant Tracking Dashboard providing real-time operational intelligence for operator oversight, featuring live driver deployment status, full utilisation metrics, accommodation occupancy tracking, shift patterns analysis, and complete check-in/check-out history with geolocation stamps',
        'Advanced candidate pipeline management system showing real-time progress through onboarding stages, assessment results, training completion status, and compliance verification milestones',
        'Driver ID Cards with scannable QR codes linking to comprehensive driver profiles including qualifications, route certifications, performance metrics, and current deployment status',
        'Route knowledge database accessible via QR system with interactive maps, traffic updates, depot information, and real-time operational alerts',
        'Seamless integration capabilities with existing DAS systems, payroll platforms, and third-party operational management tools'
      ],
      customisation: 'ATS configured in-house and can be tailored to meet specific data requirements.',
      future: 'Future development includes route integration and depot analytics dashboard capabilities.',
      integratedCapability: 'These system capabilities provide Transport UK with practical visibility of the mobilisation pipeline. Licence validation, training progression, deployment status and accommodation allocation (where applicable) can be monitored in real time, supporting informed decision-making and operational control during early franchise implementation.'
    }
  },
  {
    id: 'compliance',
    title: 'Compliance Framework',
    icon: Shield,
    content: {
      principle: '"Powered by People, Driven by Compliance" - our guiding principle reflecting strong commitment to regulatory standards.',
      contribution: 'Frequent contributions to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff.',
      payment: 'All drivers are engaged via PAYE or FCSA-accredited Umbrella partnerships.',
      standards: 'Our compliance framework includes internal audit checkpoints, right-to-work verification, DVLA API licence validation and documented onboarding sign-off stages, providing clear and auditable assurance aligned with public sector governance expectations.'
    }
  },
  {
    id: 'committed-partner',
    title: 'Committed Partner',
    icon: Award,
    content: {
      overview: 'Members of our leadership team have direct experience supporting a major UK bus franchising mobilisation. We are not currently engaged with any competing operator for the supply of temporary drivers in this region. This gives us access to a fresh, high-quality pool of candidates and, importantly, means we will not face any conflict of interest when allocating resources. Unlike agencies that support multiple operators simultaneously, we will not be required to choose which contract to prioritise. Our sole focus will be on supporting Transport UK in delivering a stable mobilisation for Huddersfield depot operations. As the only large specialist in this position, we are uniquely positioned to provide our full commitment and deliver a successful mobilisation for Transport UK.'
    }
  },
  {
    id: 'testimonials',
    title: 'Client Testimonials',
    icon: Quote,
    content: {
      overview: 'We work closely with a range of UK bus operators and transport providers, supporting both mobilisation projects and ongoing driver supply. The following feedback reflects our approach to delivery, compliance and operational support.',
      testimonials: [
        {
          company: 'Whippet Coaches',
          quote: 'C9 support Whippet\'s wider recruitment activity, providing high-quality drivers who have already been through an initial selection and vetting process. We have confidence in the C9 team to continue this, working closely with our operations team to ensure any urgent or specific requirements are met.'
        },
        {
          company: 'Metroline',
          quote: 'C9 have supported the deployment of bus drivers across high-value contracts in London, where there is a high threshold for performance. They consistently engage professionally, allocating appropriate resources to each project with strong attention to detail. The team fully understand operational requirements and take a proactive approach to delivery. We now see them as a valued partner rather than a contractor.'
        },
        {
          company: 'Specialist Passenger Solutions',
          quote: 'During a period of significant and rapid expansion within our business, C9 Recruitment has been an exceptional partner in helping us grow our team of bus drivers. Their approach is transparent, dependable and accountable, both throughout the recruitment process and in the ongoing support of the drivers they place with us. We have worked with C9 under demanding deadlines and they have repeatedly exceeded expectations. The management team regularly visits our depot to engage directly with drivers, ensuring they feel supported and valued, which strengthens both operational performance and retention. C9 has played a vital role in enabling our growth and we highly recommend their support.'
        }
      ]
    }
  }
]

// Dashboard Card Component
export function DashboardCard({ section, theme, size, searchTerm, isExpanded: controlledExpanded, onToggleExpand }) {
  // Support both controlled and uncontrolled modes
  const [internalExpanded, setInternalExpanded] = useState(false)
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded
  const handleToggle = () => {
    if (onToggleExpand) {
      onToggleExpand()
    } else {
      setInternalExpanded(!internalExpanded)
    }
  }
  const Icon = section.icon

  const highlightText = (text) => {
    if (!searchTerm || !text) return text
    
    try {
      // Escape special regex characters but preserve the search term's meaning
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
      return text.replace(regex, '<mark class="bg-yellow-300 text-yellow-900 px-1 py-0.5 rounded font-medium">$1</mark>')
    } catch (error) {
      // If regex fails, return original text
      return text
    }
  }

  const getThemeClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:from-blue-100 hover:to-blue-150'
      case 'expertise':
        return 'bg-gradient-to-br from-c9-orange/10 to-c9-orange/20 border-orange-200 hover:from-c9-orange/20 hover:to-c9-orange/30'
      case 'operations':
        return 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:from-purple-100 hover:to-purple-150'
      case 'execution':
        return 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:from-green-100 hover:to-green-150'
      case 'support':
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 hover:from-gray-100 hover:to-gray-150'
      default:
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
    }
  }

  const getIconColor = () => {
    switch (theme) {
      case 'strategy': return 'text-blue-600'
      case 'expertise': return 'text-c9-orange'
      case 'operations': return 'text-purple-600'
      case 'execution': return 'text-green-600'
      case 'support': return 'text-gray-600'
      default: return 'text-c9-blue'
    }
  }

  const getSizeClasses = () => {
    return 'min-h-[300px]'
  }

  const getButtonClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
      case 'expertise':
        return 'bg-c9-orange text-white hover:bg-orange-600 hover:shadow-lg'
      case 'operations':
        return 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
      case 'execution':
        return 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
      case 'support':
        return 'bg-gray-600 text-white hover:bg-gray-700 hover:shadow-lg'
      default:
        return 'bg-c9-blue text-white hover:bg-blue-700 hover:shadow-lg'
    }
  }

  const getCardSummary = () => {
    switch (section.id) {
      case 'objective':
        return 'Ensure the timely mobilisation of a fully trained, compliant and route-ready driver workforce to support Transport UK operations at Huddersfield depot commencing April 2027. This includes delivering drivers who meet all regulatory and operational requirements, retaining flexibility to respond to TUPE outcomes, and providing a dependable, low-risk mobilisation solution that safeguards service continuity during a period of significant structural change.'
      case 'position':
        return 'The Huddersfield depot mobilisation represents a requirement for approximately 70 drivers to support operations commencing April 2027. Mobilisation planning must account for recruitment timelines, potential TUPE outcomes and short-notice workforce fluctuations. Our approach focuses on controlled onboarding, scalable supply and clear operational visibility to ensure service continuity from day one.'
      case 'expertise':
        return 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, supported by a proven track record across complex, multi-depot operations. Members of our leadership team were directly involved in supporting a major UK bus franchising transition. While the driver market in West Yorkshire differs from that experience, the operational approach, mobilisation structure and lessons learned remain directly applicable to this requirement.'
      case 'mobilisation':
        return 'Approximately 71 drivers required for Huddersfield depot. Driver commencement: Sunday 4 April 2027, with onboarding approximately two weeks prior to service launch. Our mobilisation model is underpinned by defined onboarding gateways, staged compliance verification and structured deployment planning. Each driver progresses through controlled assessment checkpoints, ensuring regulatory assurance and operational readiness before route allocation.'
      case 'attraction':
        return 'Multi-channel driver attraction campaign designed to meet the scale and timeline of the Huddersfield depot mobilisation. Our strategy targets local PCV drivers, drivers displaced from depot closures, inactive PCV holders and national relocation candidates. Channels include digital advertising, job fairs, driver referral programmes and strategic industry partnerships with PCV training schools.'
      case 'relocation':
        return 'Fully managed relocation offering to widen the available talent pool and attract experienced drivers from outside the immediate region. Our relocation model has been successfully deployed on previous large-scale mobilisation projects, providing additional workforce flexibility where local driver supply is constrained. Accommodation paid for and managed by C9 Recruitment and invoiced back at cost.'
      case 'management':
        return 'Robust and proactive account management structure to ensure a seamless partnership with Transport UK for the Huddersfield depot mobilisation. Our approach includes a dedicated mobilisation team, on-site management presence, real-time reporting, 24/7 operational support and continuous monitoring. Alfie (Chief Executive) leads strategic delivery while Mustafa Ali (Client Success Manager) serves as dedicated Account Manager, supported by cross-departmental team members from Compliance, Relocation & Housing, Marketing, and Payroll.'
      case 'assessment':
        return 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment. This includes document verification, on-road driving assessment, induction covering depot procedures, route learning and mentoring with experienced route guides. Every deployed driver is compliance-cleared, route-ready and performance-assured prior to entering live service.'
      case 'proposition':
        return 'Competitive and attractive driver proposition designed to attract and retain quality drivers throughout the mobilisation period. Our offering includes guaranteed 50 hours per week, structured shift patterns aligned to depot operations, ongoing work stability and competitive pay aligned to the West Yorkshire driver market. Driver rates and full cost breakdown are detailed within the accompanying rate card.'
      case 'rate-card':
        return 'To support Transport UK\'s commercial modelling for the Huddersfield mobilisation, the following rate structure provides a clear breakdown of wage costs, statutory contributions and charge rates. Flat Rate: £16.50 pay rate, £24.91 charge rate. Accommodation Allowance: £168.00 pay rate, £199.08 charge rate. Rates reflect current market conditions and may require adjustment closer to mobilisation.'
      case 'training':
        return 'Optional training partnership enhancement with Ridgeway Training and Beverley Bell Consulting & Training. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader specialised services. Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector. This optional partnership offers expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers, with comprehensive course offerings including OLAT, TM CPC, First Aid, Mental Health awareness, Driver CPC modules, and e-learning platforms in development.'
      case 'supply':
        return 'Structured supply model with minimum four-month driver engagement period, aligning with Transport UK\'s anticipated contract duration while permanent recruitment is undertaken. All drivers supplied under this mobilisation will operate on a guaranteed 50-hour working week to ensure consistency, availability and operational stability during the transition period. Our approach includes 3-6 week assessment and onboarding phases, followed by active mobilisation support while TUPE drivers complete transfer and training. Driver numbers can then be reduced in a controlled and phased manner as permanent recruitment is completed.'
      case 'technology':
        return 'Technology integration providing comprehensive workforce management and real-time operational intelligence. Our system features automated DVLA API integration, a live Applicant Tracking Dashboard, QR-coded driver ID cards, candidate pipeline management and seamless integration with existing DAS systems and payroll platforms. These capabilities provide Transport UK with practical visibility of the mobilisation pipeline, supporting informed decision-making and operational control.'
      case 'compliance':
        return 'Our guiding principle "Powered by People, Driven by Compliance" reflects our unwavering commitment to regulatory standards and best practices. We frequently contribute to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff. All drivers are engaged via PAYE or FCSA-accredited Umbrella partnerships. Our compliance framework includes internal audit checkpoints, right-to-work verification, DVLA API licence validation and documented onboarding sign-off stages, providing clear and auditable assurance aligned with public sector governance expectations.'
      case 'committed-partner':
        return 'Members of our leadership team have direct experience supporting a major UK bus franchising mobilisation. We are not currently engaged with any competing operator for the supply of temporary drivers in this region. This gives us access to a fresh, high-quality pool of candidates and means we will not face any conflict of interest when allocating resources. Our sole focus will be on supporting Transport UK in delivering a stable mobilisation for Huddersfield depot operations.'
      case 'testimonials':
        return 'We work closely with a range of UK bus operators and transport providers, supporting both mobilisation projects and ongoing driver supply. The following feedback reflects our approach to delivery, compliance and operational support.'
      default:
        return section.content.overview || section.content.details?.[0] || 'Comprehensive strategic planning and implementation approach covering all aspects of workforce mobilisation, operational readiness, and service delivery excellence.'
    }
  }

  return (
    <div className={`${getSizeClasses()} ${getThemeClasses()} rounded-lg border p-3 transition-all duration-300 hover:shadow-lg group h-full flex flex-col w-full`}>
      
      {/* Card Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
            <Icon className={`w-5 h-5 ${getIconColor()}`} />
          </div>
          <div>
            <h3 
              className="font-bold text-gray-800 text-base leading-tight group-hover:text-gray-900 transition-colors"
              dangerouslySetInnerHTML={{ __html: highlightText(section.title) }}
            />
            <div className="text-xs text-gray-600 mt-0.5">
              {theme === 'strategy' && 'Strategic Planning'}
              {theme === 'expertise' && 'Core Capabilities'}
              {theme === 'operations' && 'Operational Excellence'}
              {theme === 'execution' && 'Implementation'}
              {theme === 'support' && 'Technical Systems'}
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Preview Content */}
      <div className={`flex flex-col ${isExpanded ? '' : 'h-full'}`}>
        {/* Brief Summary */}
        <div className={`${isExpanded ? 'mb-4' : 'flex-grow mb-4'}`}>
          <div
            className="text-sm text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: highlightText(getCardSummary())
            }}
          />
        </div>

        {/* Expanded Content - appears right after summary when expanded */}
        {section.id !== 'committed-partner' && isExpanded && (
          <div className="mb-4 pt-4 border-t border-white/50 animate-fade-in">
            <SectionContent section={section} highlightText={highlightText} />
          </div>
        )}

        {/* Show Detail / Collapse Button - Always at bottom (hidden for committed-partner) */}
        {section.id !== 'committed-partner' && (
          <div className={isExpanded ? '' : 'mt-auto'}>
            {!isExpanded ? (
              <button
                className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                  getButtonClasses()
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggle()
                }}
              >
                <span>SHOW DETAILS</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <button
                className="w-full py-2 px-4 rounded-lg font-medium text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 flex items-center justify-center gap-2"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggle()
                }}
              >
                <ChevronUp className="w-4 h-4" />
                <span>COLLAPSE</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Section Content Component
export function SectionContent({ section, highlightText }) {
  const { content } = section

  switch (section.id) {
    case 'objective':
      return (
        <div className="space-y-3">
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">This includes:</h4>
            <ul className="space-y-1.5">
              {content.details.map((detail, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(detail) }}></span>
                </li>
              ))}
            </ul>
          </div>
          {content.context && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Why This Matters:</h4>
              <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.context) }}></p>
            </div>
          )}
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">What Differentiates C9:</h4>
            <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.summary) }}></p>
          </div>
        </div>
      )

    case 'position':
      return (
        <div className="space-y-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg border border-red-100">
            <h4 className="font-semibold text-red-800 mb-2 text-sm">Strategic Risks Identified:</h4>
            <ul className="space-y-1.5">
              {content.risks.map((risk, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-red-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(risk) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">C9 Mitigation Framework:</h4>
            <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.mitigation) }}></p>
          </div>
        </div>
      )

    case 'mobilisation':
      return (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            {content.requirements.map((req, idx) => (
              <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <dt className="font-semibold text-c9-blue text-sm" dangerouslySetInnerHTML={{ __html: highlightText(req.label) }}></dt>
                <dd className="text-neutral-700 mt-1 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(req.value) }}></dd>
              </div>
            ))}
          </div>
          {content.mobilisationModel && (
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 mt-4">
              <h4 className="font-semibold text-c9-blue mb-2">Our Mobilisation Model:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisationModel) }}></p>
            </div>
          )}
        </div>
      )

    case 'expertise':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Key Achievements:</h4>
              <ul className="space-y-2">
                {content.achievements.map((achievement, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(achievement) }}></span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Franchising Mobilisation Experience:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.experience) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">Proven Scale & Performance:</h4>
                <p className="text-sm text-purple-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.beeNetworkMetrics) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Our Approach:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.innovation) }}></p>
              </div>
            </div>
          </div>
        </div>
      )

    case 'attraction':
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Primary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.primary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Secondary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.secondary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Attraction Channels:</h4>
              <ul className="space-y-2">
                {content.channels.map((channel, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(channel) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )

    case 'relocation':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Proven Model:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.model) }}></p>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Accommodation Features:</h4>
              <ul className="space-y-2">
                {content.accommodation.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(feature) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Driver Identification:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.identification) }}></p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2 text-sm">Safety & Compliance:</h4>
            <p className="text-sm text-red-700" dangerouslySetInnerHTML={{ __html: highlightText(content.safetyCompliance) }}></p>
          </div>
        </div>
      )

    case 'assessment':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.stage1.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage1.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.stage2.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage2.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.induction.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.induction.details) }}></p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">{content.route_learning.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.route_learning.details) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">{content.mentoring.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mentoring.details) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <h4 className="font-semibold text-green-800 mb-2 text-sm">{content.signoff.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.signoff.details) }}></p>
              </div>
            </div>
          </div>
          {content.structuredProcess && (
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 mt-4">
              <p className="text-sm text-gray-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.structuredProcess) }}></p>
            </div>
          )}
        </div>
      )

    case 'proposition':
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.description) }}></p>
          {content.benefits && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">This includes:</h4>
              <ul className="space-y-1.5">
                {content.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(benefit) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {content.marketNote && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.marketNote) }}></p>
            </div>
          )}
        </div>
      )

    case 'rate-card':
      return (
        <div className="space-y-4">
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.umbrellaNote) }}></p>
          </div>

          {/* Rate Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-c9-blue text-white">
                  <th className="px-3 py-2 text-left rounded-tl-lg">Type</th>
                  <th className="px-3 py-2 text-right">Pay Rate</th>
                  <th className="px-3 py-2 text-right">Holiday Pay</th>
                  <th className="px-3 py-2 text-right">NI</th>
                  <th className="px-3 py-2 text-right">App. Levy</th>
                  <th className="px-3 py-2 text-right">Pension</th>
                  <th className="px-3 py-2 text-right">Cost of Wage</th>
                  <th className="px-3 py-2 text-right">Mgmt Fee</th>
                  <th className="px-3 py-2 text-right rounded-tr-lg font-bold">Charge Rate</th>
                </tr>
              </thead>
              <tbody>
                {content.rateTable.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 font-semibold text-c9-blue border-b">{row.type}</td>
                    <td className="px-3 py-2 text-right border-b">{row.payRate}</td>
                    <td className="px-3 py-2 text-right border-b">{row.holidayPay}</td>
                    <td className="px-3 py-2 text-right border-b">{row.nationalInsurance}</td>
                    <td className="px-3 py-2 text-right border-b">{row.apprenticeshipLevy}</td>
                    <td className="px-3 py-2 text-right border-b">{row.pension}</td>
                    <td className="px-3 py-2 text-right border-b font-medium">{row.costOfWage}</td>
                    <td className="px-3 py-2 text-right border-b">{row.managementFee}</td>
                    <td className="px-3 py-2 text-right border-b font-bold text-c9-orange">{row.chargeRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Notes:</h4>
            <ul className="space-y-1.5">
              {content.notes.map((note, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700">{note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )

    case 'training':
      return (
        <div className="space-y-4">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-6">
            <h4 className="font-semibold text-amber-800 mb-2 text-sm">Optional Service Enhancement</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.availability) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Partnership Structure:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.partnership) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Leadership Excellence:</h4>
                <p className="text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: highlightText(content.leadership) }}></p>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.expertise) }}></p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Comprehensive Training Courses:</h4>
              <ul className="space-y-2">
                {content.courses.map((course, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(course) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Note:</h4>
            <p className="text-gray-700">The detailed Assessment & Training Process is covered in the Operations section above. This section focuses on the optional training partnership and course offerings available to enhance service delivery.</p>
          </div>
        </div>
      )

    case 'supply':
      return (
        <div className="space-y-4">
          {content.guarantee && (
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <p className="text-sm text-gray-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.guarantee) }}></p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Commitment Period:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.commitment) }}></p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Timeline:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.timeline) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Mobilisation:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisation) }}></p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Flexibility:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.flexibility) }}></p>
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Standby Arrangements:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standby) }}></p>
          </div>
          {content.windDown && (
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <p className="text-sm text-gray-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.windDown) }}></p>
            </div>
          )}
        </div>
      )

    case 'onsite':
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Operational Presence:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.presence) }}></p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Reporting:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.reporting) }}></p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Monitoring:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.monitoring) }}></p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Support Structure:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.support) }}></p>
          </div>
        </div>
      )

    case 'technology':
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Technology Systems:</h4>
            <ul className="space-y-2">
              {content.systems.map((system, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(system) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Customisation:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.customisation) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Future Development:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.future) }}></p>
            </div>
          </div>
          {content.integratedCapability && (
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 mt-4">
              <h4 className="font-semibold text-purple-800 mb-2 text-sm">What Sets Us Apart:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.integratedCapability) }}></p>
            </div>
          )}
        </div>
      )

    case 'compliance':
      return (
        <div className="space-y-4">
          <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-c9-orange">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Our Guiding Principle:</h4>
            <p className="text-lg font-medium text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.principle) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Industry Contribution:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.contribution) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Payment Structure:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.payment) }}></p>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Compliance Framework:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standards) }}></p>
          </div>
        </div>
      )

    case 'management':
      return (
        <div className="space-y-4">
          {/* C9 Organisational Chart */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-lg border border-blue-200">
            <div className="bg-white p-6 rounded-lg shadow-lg border border-blue-200 max-w-4xl mx-auto">
  <h4 className="font-bold text-c9-blue text-2xl mb-6 text-center">C9 Transport UK Project Leadership Structure</h4>
              
              {/* Leadership Level */}
              <div className="flex justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="relative">
                    <img 
                      src="/alf.png" 
                      alt="Alfie - Chief Executive" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-c9-blue shadow-lg mx-auto"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-c9-orange text-white text-xs px-2 py-1 rounded-full font-semibold">CEO</div>
                  </div>
                  <h5 className="font-bold text-c9-blue mt-3">Alfie Rae</h5>
                  <p className="text-sm text-gray-600">Chief Executive</p>
                  <p className="text-xs text-gray-500 mt-1">Strategic Delivery & Oversight</p>
                </div>
                
                <div className="text-center">
                  <div className="relative">
                    <img 
                      src="/mus.png" 
                      alt="Mustafa Ali - Client Success Manager" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-c9-blue shadow-lg mx-auto"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-c9-orange text-white text-xs px-2 py-1 rounded-full font-semibold">CSM</div>
                  </div>
                  <h5 className="font-bold text-c9-blue mt-3">Mustafa Ali</h5>
                  <p className="text-sm text-gray-600">Client Success Manager</p>
                  <p className="text-xs text-gray-500 mt-1">Dedicated Account Manager</p>
                </div>
              </div>
              
              {/* Connection Lines */}
              <div className="flex justify-center mb-6">
                <div className="w-64 h-px bg-gradient-to-r from-c9-blue via-c9-orange to-c9-blue"></div>
              </div>
              
              {/* Supporting Departments */}
              <div className="text-center mb-4">
                <h6 className="font-semibold text-gray-700 mb-4">Supported by Full C9 Organisation</h6>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <FileCheck className="w-6 h-6 text-c9-blue mx-auto mb-2" />
                    <p className="text-xs font-semibold text-c9-blue">Compliance Team</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <Building className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-green-600">Relocation & Housing</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-purple-600">Marketing Team</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <CreditCard className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-orange-600">Payroll & Finance</p>
                  </div>
                </div>
              </div>
              
              {/* Bottom note */}
              <div className="bg-c9-blue text-white p-3 rounded-lg text-center mt-6">
                <p className="text-sm font-medium">
                  📍 Senior leadership presence maintained throughout the Huddersfield mobilisation phase
                </p>
              </div>
            </div>
          </div>

          {/* Key Leadership Summary */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Project Leadership (Supported by Full C9 Organisation)</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Alfie (Chief Executive)</strong> - Strategic delivery & oversight</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Mustafa Ali (Client Success Manager)</strong> - Dedicated Account Manager</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs text-gray-600 italic">
                  Backed by our comprehensive team including recruitment specialists, compliance officers, operations coordinators, and administrative support staff across all departments.
                </p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Support Structure</h4>
              <p className="text-gray-700 text-sm">
                Dedicated mobilisation team members from Compliance, Relocation & Housing, Marketing, and Payroll departments ensuring full-spectrum support.
              </p>
            </div>
          </div>
          {content.mobilisationLed && (
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 mt-4">
              <h4 className="font-semibold text-purple-800 mb-2">Mobilisation-Led Model:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisationLed) }}></p>
            </div>
          )}
        </div>
      )

    case 'testimonials':
      return (
        <div className="space-y-4">
          {content.testimonials.map((testimonial, idx) => (
            <div key={idx} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-start gap-3">
                <Quote className="w-6 h-6 text-c9-orange flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed italic mb-3" dangerouslySetInnerHTML={{ __html: highlightText(testimonial.quote) }}></p>
                  <p className="text-sm font-semibold text-c9-blue">— {testimonial.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )

    default:
      return (
        <div className="text-gray-600">
          Content for {section.title} is being prepared...
        </div>
      )
  }
} 